const STORAGE_KEY = "lotq_order_origin_v1";
const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;

const clean = (value, max = 180) => String(value || "").trim().slice(0, max);

function normalizeSource(source, medium) {
  const normalized = clean(source, 80).toLowerCase();
  const normalizedMedium = clean(medium, 80).toLowerCase();
  if (["facebook", "fb", "ig", "instagram"].includes(normalized) && ["paid", "cpc", "ppc", "ads"].includes(normalizedMedium)) return "meta";
  return normalized;
}

function classifyReferrer(referrer) {
  const value = clean(referrer, 500).toLowerCase();
  if (!value) return { source: "direct", medium: "direct" };
  if (value.includes("instagram.com")) return { source: "instagram", medium: "organic" };
  if (value.includes("facebook.com") || value.includes("fb.com")) return { source: "facebook", medium: "organic" };
  if (value.includes("linktr.ee")) return { source: "linktree", medium: "referral" };
  if (value.includes("google.") || value.includes("bing.com")) return { source: "google", medium: "organic_search" };
  if (value.includes("whatsapp.com") || value.includes("wa.me")) return { source: "whatsapp", medium: "organic" };
  return { source: "direct", medium: "direct" };
}

function safelyStoreOrigin(origin) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(origin));
  } catch {
    // Private browsing or hardened browser settings must not block the app.
  }
}

function shouldReplaceDirectOrigin(existing, referrerOrigin) {
  return existing?.source === "direct" && referrerOrigin.source !== "direct";
}

export function captureOrderOrigin(location = window.location, referrer = document.referrer) {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(location.search || "");
  const utmSource = clean(params.get("utm_source"), 80);
  const fbclid = clean(params.get("fbclid"), 500);
  const gclid = clean(params.get("gclid"), 500);
  const hasClickOrigin = Boolean(utmSource || fbclid || gclid);
  const existing = getStoredOrderOrigin();
  const referrerOrigin = classifyReferrer(referrer);
  if (!hasClickOrigin && existing && !shouldReplaceDirectOrigin(existing, referrerOrigin)) return existing;
  const medium = clean(params.get("utm_medium"), 80).toLowerCase();
  let source = normalizeSource(utmSource, medium);
  let resolvedMedium = medium;
  if (!source && fbclid) { source = "meta"; resolvedMedium = "paid"; }
  if (!source && gclid) { source = "google"; resolvedMedium = "paid"; }
  const captured = {
    source: source || referrerOrigin.source,
    medium: resolvedMedium || referrerOrigin.medium,
    campaign: clean(params.get("utm_campaign")),
    content: clean(params.get("utm_content")),
    term: clean(params.get("utm_term")),
    fbclid,
    landing_page: `${location.pathname || "/"}${location.search || ""}`.slice(0, 1000),
    captured_at: new Date().toISOString(),
  };
  safelyStoreOrigin(captured);
  return captured;
}

export function getStoredOrderOrigin() {
  if (typeof window === "undefined") return null;
  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "null");
    const capturedAt = new Date(saved?.captured_at || 0).getTime();
    if (!saved || !capturedAt || Date.now() - capturedAt > THIRTY_DAYS) {
      try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* Storage can be disabled. */ }
      return null;
    }
    return saved;
  } catch {
    return null;
  }
}

export function getOrderOriginHeader() {
  if (typeof window === "undefined") return "";
  const origin = getStoredOrderOrigin() || classifyReferrer(typeof document === "undefined" ? "" : document.referrer);
  const payload = origin.captured_at ? origin : {
    ...origin,
    campaign: "", content: "", term: "", fbclid: "", landing_page: window.location.pathname, captured_at: new Date().toISOString(),
  };
  return encodeURIComponent(JSON.stringify(payload));
}

export function initializeOrderOriginTracking() {
  if (typeof window === "undefined") return;
  try {
    captureOrderOrigin();
  } catch {
    // Attribution is optional; loading the site is not.
  }
}
